import React, { useEffect, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useAccessContext } from '../../context/AccessContext'
import { useTheme } from '../../context/ThemeContext'
import { getLocaleName, supportedLocales, useI18n, type Locale } from '../../i18n/I18nContext'
import { getUnreadNotificationCount } from '../../services/access'
import { useIndustryTerminology } from '../../hooks/useIndustryTerminology'
import { SecuritySessions } from '../auth/SecuritySessions'
import { CommandPalette } from '../ui/CommandPalette'

export const Navigation: React.FC = () => {
  const { user, logout } = useAuth()
  const { accessContext, currentOrganization, currentBrandName, currentLogoUrl, currentBannerUrl } = useAccessContext()
  const { theme, toggleTheme } = useTheme()
  const { locale, setLocale, t } = useI18n()
  const industryLabels = useIndustryTerminology()
  const [unreadCount, setUnreadCount] = useState(0)
  const [sessionsOpen, setSessionsOpen] = useState(false)
  const membership = accessContext?.tenantMemberships?.find((item) => item.tenant_id === accessContext.tenantId)
  const membershipLabel = membership?.membership_type
    ? membership.membership_type.replace(/_/g, ' ')
    : user?.role || t('role unavailable')
  const locationCount = accessContext?.accessibleLocations?.length || 0
  const headerDescription = industryLabels.explicitIndustry
    ? `Shared operations controls for organizations, access, ${industryLabels.label('workers').toLowerCase()}, ${industryLabels.label('jobs').toLowerCase()}, and operations.`
    : t('Shared Agency SaaS controls for organizations, access, workers, jobs, and operations.')
  const displayedUnreadCount = unreadCount > 99 ? '99+' : String(unreadCount)

  useEffect(() => {
    if (!accessContext?.tenantId || !accessContext?.currentUser?.id) return
    let active = true
    getUnreadNotificationCount()
      .then((result) => {
        if (active) setUnreadCount(result.count)
      })
      .catch(() => {
        if (active) setUnreadCount(0)
      })
    return () => {
      active = false
    }
  }, [accessContext?.tenantId, accessContext?.currentUser?.id])

  return (
    <>
    <header className="top-nav" data-testid="main-nav">
      <div className="container top-nav__inner">
        <div className="stack top-nav__brand" style={{ gap: '0.35rem' }}>
          <div className="inline-group" style={{ alignItems: 'center' }}>
            {currentLogoUrl ? (
              <img
                src={currentLogoUrl}
                alt=""
                className="admin-brand-logo admin-brand-logo--sm"
                data-testid="brand-logo"
              />
            ) : (
              <span
                className="admin-brand-logo admin-brand-logo--sm admin-brand-logo--fallback"
                data-testid="brand-logo-fallback"
                aria-hidden="true"
              >
                {(currentBrandName || 'P').slice(0, 2).toUpperCase()}
              </span>
            )}
            <div className="heading-xl">{currentBrandName || t('Admin Workspace')}</div>
          </div>
          <p className="text-muted">{headerDescription}</p>
        </div>

        <div className="inline-group top-nav__actions">
          <div className="admin-user-summary">
            <p style={{ margin: 0, fontWeight: 700 }}>{accessContext?.currentUser?.email || user?.email || t('Unknown user')}</p>
            <p className="text-muted admin-user-summary__detail" style={{ margin: 0, fontSize: '0.8rem' }}>
              {membershipLabel}
            </p>
            <p className="text-muted admin-user-summary__detail" style={{ margin: 0, fontSize: '0.8rem' }} data-testid="admin-header-org">
              {currentOrganization
                ? `${currentOrganization.name} - ${currentOrganization.org_type.replace(/_/g, ' ')}`
                : accessContext?.isSystemSuperAdmin || accessContext?.isPlatformAdmin
                  ? accessContext?.tenant
                    ? `${t('Tenant context')} — ${accessContext.tenant.name || accessContext.tenant.slug || accessContext.tenantId}`
                    : t('Platform scope — all tenants')
                  : t('Organization context unavailable')}
            </p>
            {locationCount ? (
              <p className="text-muted admin-user-summary__detail" style={{ margin: 0, fontSize: '0.8rem' }}>
                {locationCount === 1
                  ? (industryLabels.explicitIndustry ? `1 visible ${industryLabels.label('location').toLowerCase()}` : t('1 visible location'))
                  : `${locationCount} ${industryLabels.explicitIndustry ? `visible ${industryLabels.label('locations').toLowerCase()}` : t('visible locations')}`}
              </p>
            ) : null}
          </div>
          <div className="top-nav__controls">
            <CommandPalette />
            <label className="sr-only" htmlFor="admin-language-select">{t('Language')}</label>
            <select
              id="admin-language-select"
              className="input"
              aria-label={t('Language')}
              value={locale}
              onChange={(event) => setLocale(event.target.value as Locale)}
              data-testid="language-toggle"
            >
              {supportedLocales.map((item) => (
                <option key={item} value={item}>{getLocaleName(item)}</option>
              ))}
            </select>
            {unreadCount > 0 ? (
              <span
                className="notification-badge"
                aria-label={`${displayedUnreadCount} ${t('unread notifications')}`}
                title={`${displayedUnreadCount} ${t('unread notifications')}`}
                data-testid="notification-badge"
              >
                {displayedUnreadCount}
              </span>
            ) : null}
            <button
              onClick={toggleTheme}
              className="btn btn-ghost"
              aria-pressed={theme === 'dark'}
              aria-label={theme === 'dark' ? t('Switch to light') : t('Switch to dark')}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? t('Switch to light') : t('Switch to dark')}
            </button>
            <button type="button" onClick={() => setSessionsOpen(true)} className="btn btn-ghost" data-testid="security-sessions-button">
              {t('Security')}
            </button>
            <button onClick={logout} className="btn btn-primary" data-testid="signout-button">{t('Sign out')}</button>
          </div>
        </div>
      </div>
    </header>
    {currentBannerUrl ? (
      <div className="brand-banner-shell" data-testid="brand-banner">
        <img
          src={currentBannerUrl}
          alt=""
          className="brand-banner-img"
          onError={(e) => { e.currentTarget.style.display = 'none' }}
        />
      </div>
    ) : null}
    {sessionsOpen ? <SecuritySessions onClose={() => setSessionsOpen(false)} /> : null}
    </>
  )
}
